// src/services/PDFThumbnailService.ts
import { pdfjs } from 'react-pdf';

// Ensure worker is set up
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs`;
}

/**
 * Generates a thumbnail for PDF files using react-pdf
 * @param pdfFile The PDF file to generate a thumbnail for
 * @returns Promise resolving to a data URL of the thumbnail
 */
export async function generatePDFThumbnail(pdfFile: File): Promise<string> {
  try {
    // Create a container element for rendering
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    document.body.appendChild(container);

    // Use PDF.js directly to render the first page
    const arrayBuffer = await pdfFile.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    
    // Check if PDF loaded successfully and has pages
    if (pdf.numPages < 1) {
      throw new Error('PDF has no pages');
    }
    
    // Get the first page
    const page = await pdf.getPage(1);
    
    // Set up canvas for rendering
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get canvas context');
    }
    
    // Calculate dimensions (scale to 100px width while maintaining aspect ratio)
    const viewport = page.getViewport({ scale: 1.0 });
    const scale = 100 / viewport.width;
    const scaledViewport = page.getViewport({ scale });
    
    // Set canvas dimensions
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    
    // Render PDF page to canvas
    await page.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;
    
    // Clean up
    document.body.removeChild(container);
    
    // Return the canvas data URL
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl;
  } catch (error) {
    console.error('Error generating PDF thumbnail with react-pdf:', error);
    
    // Return default PDF icon if anything fails
    return '/file-icons/pdf.svg';
  }
}